/*
 * Copyright (c) 2020. American Express Company
 */

import * as _ from 'lodash';
import {
    ChronoUnit,
    DateTimeFormatter,
    Duration,
    LocalDate,
    LocalDateTime,
    nativeJs,
    Period,
    TemporalAdjusters,
    ZoneId
} from '@js-joda/core';
import * as Moment from 'moment';
import { extendMoment } from 'moment-range';
import FormatService from '../services/format.service';
import firstDayOfMonth = TemporalAdjusters.firstDayOfMonth;
import firstDayOfYear = TemporalAdjusters.firstDayOfYear;
import { PageTimePeriod } from 'src/types/page-time-period.enum';

const moment = extendMoment(Moment);

export default class DateUtils {
    public static startOfLastYear(): Moment.Moment {
        return moment({ hour: 0, day: 0, month: 0, year: moment().year() - 1 });
    }

    public static startOfThisMonth(): Moment.Moment {
        return moment({ hour: 0, day: 1 });
    }

    public static startDateOfLastMonth(): LocalDate {
        return this.startDateOfThisMonth().minusMonths(1);
    }

    public static endDateOfLastMonth(): LocalDate {
        return this.startDateOfThisMonth().minusDays(1);
    }

    public static startDateOfThisMonth(): LocalDate {
        return LocalDate.now().with(firstDayOfMonth());
    }

    public static startDateOfThisMonthLastYear(): LocalDate {
        return this.startDateOfThisMonth().minusYears(1);
    }

    public static startDateOfNextMonth(): LocalDate {
        return this.startDateOfThisMonth().plusMonths(1);
    }

    public static startDateOfLastYear(): LocalDate {
        return LocalDate.now().minusYears(1).with(firstDayOfYear());
    }

    public static startDateOfThisYear(): LocalDate {
        return LocalDate.now().with(firstDayOfYear());
    }

    public static daysAgo(days: number): LocalDate {
        return LocalDate.now().minusDays(days);
    }

    public static daysAhead(days: number): LocalDate {
        return LocalDate.now().plusDays(days);
    }

    public static yearsAgo(years: number): LocalDate {
        return LocalDate.now().minusYears(years);
    }

    public static isLastMonthOfYear(): boolean {
        return LocalDate.now().monthValue() === 12;
    }

    public static nearestMonthStartFromDate(date: Date): Date {
        // force this to be the first of the next month or current month depending on dayOfMonth
        const localDate = this.nearestMonthStartFromLocalDate(LocalDate.parse(moment(date).format('YYYY-MM-DD')));
        return moment(localDate.withDayOfMonth(1).toString()).toDate();
    }

    public static nearestMonthStartFromMoment(momentDate: Moment.Moment): Moment.Moment {
        // force this to be the first of the next month or current month depending on dayOfMonth
        const localDate = this.nearestMonthStartFromLocalDate(LocalDate.parse(momentDate.format('YYYY-MM-DD')));
        return moment(localDate.withDayOfMonth(1).toString());
    }

    public static quarterOf(date: LocalDate): number {
        if (!date) return;
        return Math.ceil(date.monthValue() / 3);
    }

    public static startOfQuarter(date: LocalDate): LocalDate {
        const startMonth = (this.quarterOf(date) - 1) * 3 + 1;
        return date.withMonth(startMonth);
    }

    public static toDateObject(date: LocalDate | string): Date {
        if (!date) {
            return undefined;
        } else if (_.isString(date)) {
            if (date.length > 10) {
                return moment(LocalDate.parse(moment(date).format('YYYY-MM-DD')).toString()).toDate();
            }
            return moment(LocalDate.parse(date).toString()).toDate();
        } else {
            return moment(date.toString()).toDate();
        }
    }

    public static dateify(date: LocalDate | string): LocalDate {
        if (!date) {
            return undefined;
        } else if (_.isString(date)) {
            if (date.length > 8) {
                return LocalDate.parse(moment(date).format('YYYY-MM-DD'));
            }
            return LocalDate.parse(date);
        } else {
            return date;
        }
    }

    public static datetimeify(date: LocalDateTime | string): LocalDateTime {
        if (!date) {
            return undefined;
        } else if (_.isString(date)) {
            // convert from server time to local time
            const rawTime = LocalDateTime.parse(date.substring(0, 19));
            const utcZonedTime = rawTime.atZone(ZoneId.of('Z'));
            const localZonedTime = rawTime.atZone(ZoneId.systemDefault());
            const diff = Duration.between(localZonedTime, utcZonedTime);
            return rawTime.plus(diff);
        } else {
            return date;
        }
    }

    /**
     * Number of quarters from d1 to d2
     * @param d1
     * @param d2
     */
    public static quartersUntil(d1: LocalDate, d2: LocalDate): number {
        const startOfQ1 = this.startOfQuarter(d1);
        const startOfQ2 = this.startOfQuarter(d2);
        const monthsUntil = startOfQ1.until(startOfQ2, ChronoUnit.MONTHS);
        return Math.floor(monthsUntil / 3);
    }

    public static nearestMonthStartFromLocalDate(localDate: LocalDate): LocalDate {
        // force this to be the first of the next month or current month depending on dayOfMonth
        const dayOfMonth: number = localDate.dayOfMonth();
        if (dayOfMonth === 1) {
            return localDate;
        }
        if (dayOfMonth > 15) {
            // this is supposed to do the right thing even if it's march 31 (there is no feb 31)
            localDate = localDate.plusMonths(1);
        }
        return localDate.withDayOfMonth(1);
    }

    /**
     * convert a Date to a LocalDate
     * Since TypeORM stuffs String's into Entity fields typed as Date, unless there's a ValueTransformer, explicitly
     * handle String dates.
     */
    public static fromDateToLocalDate(value: Date | String): LocalDate {
        if (!value) {
            return null;
        } else if (typeof value === 'string' || value instanceof String) {
            return LocalDate.parse(value.slice(0, 10));
        } else {
            const result = new Date(value);
            result.setMinutes(result.getMinutes() + result.getTimezoneOffset());
            return LocalDate.parse(result.toISOString().slice(0, 10));
        }
    }

    public static simpleDateToLocalDate(date: Date): LocalDate {
        return LocalDate.of(date.getFullYear(), date.getMonth() + 1, date.getDate());
    }

    public static fromDateToString(date: Date): String {
        if (!date) {
            return null;
        }
        return LocalDate.from(nativeJs(date)).format(DateTimeFormatter.ISO_LOCAL_DATE);
    }

    // from month 1..12 to quarter 1..4
    public static monthToQuarter(month: number): number {
        return Math.floor((month - 1) / 3) + 1;
    }

    // from month 1..12 to (1,4,7 or 10)
    public static firstMonthOfQuarter(month: number): number {
        return Math.floor((month - 1) / 3) * 3 + 1;
    }

    public static timeSince(pastTime: LocalDateTime): string {
        const today = LocalDate.now();

        // "period" is for counting time bigger than one day
        const period = Period.between(LocalDate.from(pastTime), today);
        if (period.years() >= 1) {
            return `${period.years()} ${FormatService.pluralize(period.years(), ['year', 'years'])}`;
        }
        if (period.months() >= 1) {
            return `${period.months()} ${FormatService.pluralize(period.months(), ['month', 'months'])}`;
        }
        if (period.days() >= 1) {
            return `${period.days()} ${FormatService.pluralize(period.days(), ['day', 'days'])}`;
        }

        // "duration" counts time under one day
        const now = LocalDateTime.now();
        const duration = Duration.between(pastTime, now);
        if (duration.toHours() >= 1) {
            return `${duration.toHours()} ${FormatService.pluralize(duration.toHours(), ['hour', 'hours'])}`;
        }
        if (duration.toMinutes() >= 1) {
            return `${duration.toMinutes()} ${FormatService.pluralize(duration.toMinutes(), ['minute', 'minutes'])}`;
        }
        return `${duration.seconds()} ${FormatService.pluralize(duration.seconds(), ['second', 'seconds'])}`;
    }

    public static daysUntilNextMonth(): number {
        const today = LocalDate.now();
        const firstOfNextMonth = LocalDate.now().with(TemporalAdjusters.firstDayOfNextMonth());
        return ChronoUnit.DAYS.between(today, firstOfNextMonth);
    }

    /**
     * Start and end dates in the format MM-dd-YYYY based on the PageTimePeriod
     * @param timePeriod
     */
    public static getDatesFromTimePeriod(timePeriod: PageTimePeriod): { startDate: string; endDate: string } {
        let startDate: string, endDate: string;
        if (timePeriod === PageTimePeriod.CURRENT_MONTH) {
            startDate = FormatService.formatSqlDate(DateUtils.startDateOfThisMonth());
            endDate = FormatService.formatSqlDate(DateUtils.daysAhead(1));
        } else if (timePeriod === PageTimePeriod.LAST_YEAR) {
            startDate = FormatService.formatSqlDate(DateUtils.yearsAgo(1).withDayOfMonth(1));
            endDate = FormatService.formatSqlDate(DateUtils.startDateOfThisMonth());
        }
        return { startDate, endDate };
    }

    static dateSqlDisplayFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('yyyy-MM-dd');

    public static parseStringToDate(
        dateString: string,
        formatter: DateTimeFormatter = this.dateSqlDisplayFormatter
    ): LocalDate {
        try {
            return LocalDate.parse(dateString, formatter);
        } catch (_) {
            return null;
        }
    }
}
